/**
 * The integration hub.
 *
 * ═══════════════════════════════════════════════════════════════════
 * EVERY CONNECTOR IN THIS BUILD IS A DEMONSTRATION ADAPTER.
 *
 * None of them contacts the system it is named after. They generate
 * deterministic synthetic data so the architecture around them — mapping,
 * staging, validation, control totals, the error queue, retry with backoff,
 * health — can be demonstrated end to end without a single external credential.
 *
 * The flag is on the connector record in the database, the API returns it, and
 * this screen shows it on every row. Nothing here may present itself as a live
 * integration with anybody's real system.
 * ═══════════════════════════════════════════════════════════════════
 *
 * A record that fails validation is QUEUED, never discarded. An interface that
 * silently drops what it cannot parse is worse than one that fails loudly,
 * because the loss is only discovered at year end.
 *
 * That promise governs this screen too, not just the server: a record the
 * viewer has just acted on must still be on the screen afterwards. So the queue
 * asks the API for every error and decides here what to show, rather than
 * asking for one status and watching rows disappear the moment their status
 * changes.
 */

import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync, useMutation } from '../lib/useAsync';
import { useSession } from '../lib/session';
import { lower, useTerms } from '../lib/tenant';
import { dateTime, humanise, number, relativeTime, statusLabel, statusTone } from '../lib/format';
import {
  Alert,
  AsyncPanel,
  Badge,
  Card,
  DataTable,
  DefinitionList,
  EmptyState,
  ErrorNotice,
  Modal,
  Money,
  PageHeader,
  Stat,
  Tabs,
  TextArea,
} from '../components/ui';

interface Connector {
  id: string;
  code: string;
  name: string;
  vendor: string;
  category: string;
  description: string;
  direction: string;
  transport: string;
  transfer_mode: string;
  schedule_description: string | null;
  is_enabled: boolean;
  health_status: string;
  is_demonstration_adapter: boolean;
  last_success_at: string | null;
  last_failure_at: string | null;
  consecutive_failures: number;
  max_retries: number;
  // The connectors route casts these three counts with Number() before it
  // answers, so they arrive as numbers and not as the strings COUNT(*) would
  // otherwise produce.
  run_count: number;
  open_error_count: number;
  mapping_count: number;
}

interface IntegrationError {
  id: string;
  connector_name: string;
  connector_code: string;
  record_index: number;
  record_key: string | null;
  error_code: string;
  error_message: string;
  payload: Record<string, unknown>;
  status: string;
  retry_count: number;
  next_retry_at: string | null;
  created_at: string;
  run_reference: string | null;
  resolution_note: string | null;
  resolved_by_name: string | null;
  resolved_at: string | null;
}

interface Mapping {
  id: string;
  source_field: string;
  target_field: string;
  target_table: string | null;
  transform: string;
  transform_argument: string | null;
  is_required: boolean;
  default_value: string | null;
  notes: string | null;
}

interface Run {
  id: string;
  run_reference: string;
  trigger: string;
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  status: string;
  records_read: number;
  records_accepted: number;
  records_rejected: number;
  // The control totals are BIGINT columns. Against a real PostgreSQL cluster
  // the driver hands those back as strings so no figure can be mangled by a JS
  // float, so every use of them goes through Number().
  control_total_in_minor: number | string;
  control_total_out_minor: number | string;
  reconciled: boolean;
  error_message: string | null;
  triggered_by_name: string | null;
}

interface ConnectorDetail {
  connector: Connector;
  mappings: Mapping[];
  runs: Run[];
  openErrors: IntegrationError[];
  adapterDescription: string | null;
  isDemonstrationAdapter: boolean;
}

/** What POST /connectors/:id/run answers with — the reconciliation statement for one run. */
interface RunOutcome {
  runId: string;
  runReference: string;
  status: string;
  recordsRead: number;
  recordsAccepted: number;
  recordsRejected: number;
  controlTotalInMinor: number;
  controlTotalOutMinor: number;
  reconciled: boolean;
  errors: Array<{ recordKey: string | null; errorCode: string; errorMessage: string }>;
  notes: string[];
  /** Present only on a deliberately induced failure, explaining what was broken and why. */
  notice: string | null;
}

interface RetryResult {
  resolved: boolean;
  message: string;
  retryCount: number;
}

/* ------------------------------------------------------------------ *
 * Machine values in the words the room uses
 * ------------------------------------------------------------------ */

/**
 * humanise() only splits underscores and capitalises the first letter, which is
 * right for a status and wrong for an acronym: it turns 'rest' into "Rest",
 * 'sftp' into "Sftp" and 'hr' into "Hr". Nobody in the room reads those as the
 * things they are, and lower-case acronyms look like a defect. These are the
 * only values the column can hold, so they are spelled out.
 */
const TRANSPORT_LABELS: Record<string, string> = {
  rest: 'REST API',
  sftp: 'SFTP',
  file_upload: 'File upload',
  webhook: 'Webhook',
  database: 'Database',
  manual: 'Manual',
};

const DIRECTION_LABELS: Record<string, string> = {
  inbound: 'Inbound',
  outbound: 'Outbound',
  bidirectional: 'Two-way',
};

const CATEGORY_LABELS: Record<string, string> = {
  banking: 'Banking',
  card: 'Purchasing card',
  companies_house: 'Companies House',
  hr: 'HR',
  microsoft: 'Identity and email',
  payments: 'Payments',
  payroll: 'Payroll',
  student_records: 'Student records',
  tax: 'Tax',
};

/**
 * Where a mapped field lands, said in business words.
 *
 * The mapping rows carry the storage table, which statusLabel() would render as
 * "App user" or "Procurement card transaction" — the schema talking, not the
 * product. The field names themselves stay verbatim, because a field mapping is
 * exactly where an IT reviewer wants to see the real names.
 */
const TARGET_TABLE_LABELS: Record<string, string> = {
  absence_period: 'Absence record',
  app_user: 'User account',
  bank_transaction: 'Bank statement line',
  journal_line: 'Journal line',
  procurement_card: 'Purchasing card',
  procurement_card_transaction: 'Purchasing card transaction',
  sales_invoice_line: 'Sales invoice line',
  user_data_scope: 'User access scope',
};

const TRANSFORM_LABELS: Record<string, string> = {
  none: 'As supplied',
  trim: 'Trim spaces',
  upper: 'Upper case',
  lower: 'Lower case',
  date_iso: 'Date to ISO',
  money_minor: 'Amount to pence',
  lookup: 'Lookup',
  constant: 'Fixed value',
  concat: 'Join fields',
};

const TRIGGER_LABELS: Record<string, string> = {
  scheduled: 'Scheduled',
  manual: 'Run by hand',
  webhook: 'Webhook',
  retry: 'Retry',
  demo: 'Demonstration',
};

/** Statuses that mean the record is still waiting for somebody to decide. */
const AWAITING_DECISION = ['open', 'retrying'];

/**
 * Tones for the queue's own statuses.
 *
 * statusTone() maps document states, and it reads 'written_off' as a failure —
 * red, with the error marker. In this queue a write-off is not a failure, it is
 * the decision somebody took and recorded, so the states are toned here.
 */
const ERROR_STATUS_TONES: Record<string, 'neutral' | 'ok' | 'warn' | 'error' | 'info'> = {
  open: 'warn',
  retrying: 'info',
  resolved: 'ok',
  written_off: 'neutral',
  superseded: 'neutral',
};

const label = (map: Record<string, string>, value: string | null | undefined): string =>
  (value ? map[value] : undefined) ?? statusLabel(value);

/**
 * A scheduled instant, never an elapsed one.
 *
 * relativeTime() only counts backwards, so a retry scheduled for later today
 * reads as "just now" and one whose moment has already passed reads as
 * "20 minutes ago" — under the word "next". A scheduled attempt is either still
 * to come, in which case say when, or it is due.
 */
function nextAttempt(iso: string | null): string | null {
  if (!iso) return null;
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return null;
  return at > Date.now() ? `Next attempt ${dateTime(iso)}` : 'Next attempt due now';
}

/** A source field name as it arrived — 'feeAmount' — read as English. */
function fieldLabel(key: string): string {
  return humanise(key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase());
}

/**
 * A value from the incoming record, shown exactly as it arrived.
 *
 * Deliberately not formatted. The whole point of the disclosure is to show what
 * the far system actually sent — 'NOT SUPPLIED' where an amount was expected is
 * the evidence, and tidying it up would hide the reason the record failed.
 */
function payloadValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value.length > 0 ? value : '(empty)';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function runTone(status: string): 'ok' | 'warn' | 'error' | 'info' {
  if (status === 'succeeded') return 'ok';
  if (status === 'partial') return 'warn';
  if (status === 'failed') return 'error';
  return 'info';
}

export function IntegrationsPage() {
  const { can } = useSession();
  const t = useTerms();

  // The tab lives in the address bar so a headline figure can link straight to
  // the records behind it, and so a link to the queue survives a page reload.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'errors' ? 'errors' : 'connectors';
  const setTab = (key: string) => setSearchParams(key === 'errors' ? { tab: 'errors' } : {}, { replace: true });

  const [selected, setSelected] = useState<Connector | null>(null);
  const [runningId, setRunningId] = useState<string | null>(null);
  const [lastRun, setLastRun] = useState<{ connectorName: string; forcedFailure: boolean; outcome: RunOutcome } | null>(
    null,
  );
  const [lastRetry, setLastRetry] = useState<{ recordKey: string | null; result: RetryResult } | null>(null);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [writeOffFor, setWriteOffFor] = useState<IntegrationError | null>(null);
  const [reason, setReason] = useState('');

  const connectors = useAsync(() => api.get<{ connectors: Connector[] }>('/integrations/connectors'), []);

  // Asked for without a status filter on purpose. The route matches one status
  // exactly, so '?status=open' loses a record the moment a failed retry moves it
  // to 'retrying' — on a screen that has just promised nothing is ever dropped.
  const errors = useAsync(() => api.get<{ errors: IntegrationError[] }>('/integrations/errors'), []);

  const detail = useAsync(
    () =>
      selected
        ? api.get<ConnectorDetail>(`/integrations/connectors/${selected.id}`)
        : Promise.resolve(null as never),
    [selected?.id],
  );

  const run = useMutation(async (connectorId: string, forceFailure: boolean) =>
    api.post<RunOutcome>(`/integrations/connectors/${connectorId}/run`, { forceFailure }),
  );
  const retry = useMutation(async (errorId: string) =>
    api.post<RetryResult>(`/integrations/errors/${errorId}/retry`, {}),
  );
  const writeOff = useMutation(async (errorId: string, note: string) =>
    api.post<{ ok: boolean }>(`/integrations/errors/${errorId}/write-off`, { reason: note }),
  );

  const categoryLabel = (value: string) =>
    // The one category named after a business noun the tenant configures.
    value === 'supplier_portal' ? `${t.supplier} portal` : label(CATEGORY_LABELS, value);

  // Several mapping targets are nouns the tenant names for itself, so they are
  // resolved from the terminology rather than spelled out here.
  const targetTableLabel = (value: string) => {
    const fromTerms: Record<string, string> = {
      customer: t.customer,
      customer_receipt: `${t.customer} receipt`,
      supplier: t.supplier,
      supplier_request: `New ${lower(t.supplier)} request`,
      purchase_order: t.purchaseOrder,
      purchase_invoice: `${t.supplier} invoice`,
    };
    return fromTerms[value] ?? label(TARGET_TABLE_LABELS, value);
  };

  const startRun = (connector: Connector, forceFailure: boolean) => {
    setRunningId(connector.id);
    void run.run(connector.id, forceFailure).then((outcome) => {
      setRunningId(null);
      if (outcome) setLastRun({ connectorName: connector.name, forcedFailure: forceFailure, outcome });
      connectors.reload();
      errors.reload();
      if (selected) detail.reload();
    });
  };

  const startRetry = (record: IntegrationError) => {
    void retry.run(record.id).then((result) => {
      if (result) setLastRetry({ recordKey: record.record_key, result });
      errors.reload();
      connectors.reload();
    });
  };

  const confirmWriteOff = () => {
    if (!writeOffFor) return;
    void writeOff.run(writeOffFor.id, reason.trim()).then((result) => {
      if (!result) return;
      setWriteOffFor(null);
      setReason('');
      // A written-off record must not vanish either: the decision and its reason
      // are the record of what was done, so the closed rows are revealed.
      setIncludeClosed(true);
      errors.reload();
      connectors.reload();
    });
  };

  const allErrors = errors.data?.errors ?? [];
  const awaiting = allErrors.filter((e) => AWAITING_DECISION.includes(e.status));
  const visibleErrors = includeClosed ? allErrors : awaiting;
  const closedCount = allErrors.length - awaiting.length;

  return (
    <>
      <PageHeader
        title="Integration hub"
        subtitle="Connectors, runs, control totals and the error queue."
      />

      <Alert tone="warn" title="Every connector here is a demonstration adapter">
        <p>
          None of them contacts the system it is named after, and no external credential is needed to run any of them.
          They exist so the mapping, validation, reconciliation, error queue and retry can be demonstrated honestly.
        </p>
      </Alert>

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { key: 'connectors', label: 'Connectors' },
          { key: 'errors', label: 'Error queue', badge: awaiting.length },
        ]}
      />

      {tab === 'connectors' ? (
        <AsyncPanel state={connectors}>
          {(data) => {
            const total = data.connectors.length;
            const healthy = data.connectors.filter((c) => c.health_status === 'healthy').length;
            // The database allows healthy, degraded, failing, disabled and
            // not_run. There is no 'failed', so a tile looking for one counts a
            // broken connector as fine.
            const attention = data.connectors.filter((c) => ['degraded', 'failing'].includes(c.health_status)).length;
            const notRun = data.connectors.filter((c) => c.health_status === 'not_run').length;
            const disabled = data.connectors.filter((c) => c.health_status === 'disabled').length;
            const queued = data.connectors.reduce((s, c) => s + c.open_error_count, 0);

            return (
              <div className="stack">
                {/* The three health tiles and the disabled count add up to the
                    total, so a reader doing the arithmetic is never left with
                    connectors unaccounted for. */}
                <div className="grid grid--4">
                  <Stat label="Healthy" value={String(healthy)} detail={`of ${total} connectors`} />
                  <Stat
                    label="Degraded or failing"
                    value={String(attention)}
                    tone={attention > 0 ? 'negative' : undefined}
                    detail={attention > 0 ? 'Needs attention' : 'Nothing needing attention'}
                  />
                  <Stat
                    label="Not yet run"
                    value={String(notRun)}
                    detail={disabled > 0 ? `${number(disabled)} switched off as well` : 'No run recorded in this build'}
                  />
                  <Stat
                    label="Records in the error queue"
                    value={String(queued)}
                    detail="Queued, never discarded"
                    to="/integrations?tab=errors"
                    drillLabel="Open the queue"
                  />
                </div>

                <ErrorNotice error={run.error} />

                {lastRun ? (
                  <Alert
                    tone={runTone(lastRun.outcome.status)}
                    title={`${lastRun.connectorName} — ${lastRun.outcome.runReference} ${statusLabel(lastRun.outcome.status).toLowerCase()}`}
                  >
                    <DefinitionList
                      items={[
                        ['Records read', number(lastRun.outcome.recordsRead)],
                        ['Accepted', number(lastRun.outcome.recordsAccepted)],
                        [
                          'Queued as errors',
                          lastRun.outcome.recordsRejected > 0 ? (
                            <span className="num--negative">{number(lastRun.outcome.recordsRejected)}</span>
                          ) : (
                            '0'
                          ),
                        ],
                        ['Declared by the source', <Money minor={lastRun.outcome.controlTotalOutMinor} />],
                        ['Applied here', <Money minor={lastRun.outcome.controlTotalInMinor} />],
                        [
                          'Control totals',
                          lastRun.outcome.reconciled ? (
                            'Reconciled'
                          ) : (
                            <span>
                              A difference of{' '}
                              <Money
                                minor={Math.abs(lastRun.outcome.controlTotalOutMinor - lastRun.outcome.controlTotalInMinor)}
                              />
                            </span>
                          ),
                        ],
                      ]}
                    />
                    {lastRun.outcome.notes.map((note, i) => (
                      <p key={i} className="smaller" style={{ marginTop: '0.5rem' }}>
                        {note}
                      </p>
                    ))}
                    {/* The server attaches its "a failure was deliberately
                        induced" note to any run asked for with a failure, but a
                        few adapters have no records to fail on. Saying a failure
                        was induced beside a clean run and a count of nought
                        contradicts itself, so the note is shown only when
                        something actually did fail. */}
                    {lastRun.outcome.notice && (lastRun.outcome.recordsRejected > 0 || lastRun.outcome.status !== 'succeeded') ? (
                      <p className="smaller" style={{ marginTop: '0.5rem' }}>
                        {lastRun.outcome.notice}
                      </p>
                    ) : null}
                    {lastRun.forcedFailure &&
                    lastRun.outcome.recordsRejected === 0 &&
                    lastRun.outcome.status === 'succeeded' ? (
                      <p className="smaller" style={{ marginTop: '0.5rem' }}>
                        No failure was induced: this adapter had no records to fail on, so the run completed normally.
                      </p>
                    ) : null}
                    <p className="row" style={{ marginTop: '0.5rem' }}>
                      {lastRun.outcome.recordsRejected > 0 ? (
                        <button type="button" className="btn btn--sm" onClick={() => setTab('errors')}>
                          Show the {lastRun.outcome.recordsRejected === 1 ? 'record' : 'records'} in the queue
                        </button>
                      ) : null}
                      <button type="button" className="btn btn--sm btn--ghost" onClick={() => setLastRun(null)}>
                        Dismiss
                      </button>
                    </p>
                  </Alert>
                ) : null}

                <Card
                  title="Connectors"
                  subtitle="Select a connector to see its field mapping, its run history and the control totals for each run."
                  flush
                >
                  <DataTable
                    caption="Integration connectors"
                    rows={data.connectors}
                    rowKey={(c) => c.id}
                    onRowClick={(c) => setSelected(c)}
                    columns={[
                      {
                        key: 'name',
                        header: 'Connector',
                        sortValue: (c) => c.name,
                        render: (c) => (
                          <>
                            <div>{c.name}</div>
                            <div className="smaller muted">
                              {c.vendor} · {categoryLabel(c.category)}
                            </div>
                            {c.is_demonstration_adapter ? (
                              <div className="smaller">
                                <Badge tone="warn">Demonstration adapter</Badge>
                              </div>
                            ) : null}
                          </>
                        ),
                      },
                      {
                        key: 'flow',
                        header: 'Flow',
                        sortValue: (c) => c.direction,
                        render: (c) => (
                          <div className="smaller">
                            <div>
                              {label(DIRECTION_LABELS, c.direction)} · {label(TRANSPORT_LABELS, c.transport)}
                            </div>
                            <div className="muted">{c.schedule_description ?? 'On demand'}</div>
                          </div>
                        ),
                      },
                      {
                        key: 'mappings',
                        header: 'Mappings',
                        numeric: true,
                        sortValue: (c) => c.mapping_count,
                        render: (c) => number(c.mapping_count),
                      },
                      {
                        key: 'runs',
                        header: 'Runs',
                        numeric: true,
                        sortValue: (c) => c.run_count,
                        render: (c) => number(c.run_count),
                      },
                      {
                        key: 'errors',
                        header: 'Errors',
                        numeric: true,
                        sortValue: (c) => c.open_error_count,
                        render: (c) =>
                          c.open_error_count > 0 ? (
                            <Badge tone="error">{number(c.open_error_count)}</Badge>
                          ) : (
                            <span className="muted">—</span>
                          ),
                      },
                      {
                        key: 'health',
                        header: 'Health',
                        sortValue: (c) => c.health_status,
                        render: (c) => {
                          const failedSinceSuccess =
                            c.last_failure_at !== null &&
                            (c.last_success_at === null ||
                              Date.parse(c.last_failure_at) > Date.parse(c.last_success_at));
                          return (
                            <>
                              <Badge tone={statusTone(c.health_status)}>{statusLabel(c.health_status)}</Badge>
                              {c.last_success_at ? (
                                <div className="smaller muted">last succeeded {relativeTime(c.last_success_at)}</div>
                              ) : null}
                              {failedSinceSuccess ? (
                                <div className="smaller num--negative">last failed {relativeTime(c.last_failure_at)}</div>
                              ) : null}
                            </>
                          );
                        },
                      },
                      {
                        key: 'actions',
                        header: <span className="visually-hidden">Run</span>,
                        render: (c) =>
                          can('integration.run') ? (
                            // The row itself opens the connector, so the buttons
                            // have to keep their click to themselves.
                            <div className="btn-group" onClick={(event) => event.stopPropagation()}>
                              <button
                                type="button"
                                className="btn btn--sm"
                                disabled={run.busy}
                                onClick={() => startRun(c, false)}
                              >
                                {runningId === c.id ? 'Running…' : 'Run'}
                              </button>
                              <button
                                type="button"
                                className="btn btn--sm btn--ghost"
                                title="Run with a deliberate failure, so the error queue and retry can be seen"
                                disabled={run.busy}
                                onClick={() => startRun(c, true)}
                              >
                                Run with a failure
                              </button>
                            </div>
                          ) : null,
                      },
                    ]}
                  />
                </Card>
              </div>
            );
          }}
        </AsyncPanel>
      ) : (
        <Card
          title="Error queue"
          subtitle="Records that failed validation. They are held here for a decision, not discarded."
          actions={
            closedCount > 0 ? (
              <label className="row smaller">
                <input
                  type="checkbox"
                  checked={includeClosed}
                  onChange={(event) => setIncludeClosed(event.currentTarget.checked)}
                />
                <span>Include the {number(closedCount)} already dealt with</span>
              </label>
            ) : null
          }
          flush
        >
          <AsyncPanel state={errors}>
            {() => (
              <>
                {/* The card body is flush so the table can reach its edges, so
                    anything that is not the table has to bring its own margin. */}
                {retry.error || lastRetry ? (
                  <div style={{ padding: '1rem 1.25rem 0' }}>
                    <ErrorNotice error={retry.error} />
                    {lastRetry ? (
                      <Alert
                        tone={lastRetry.result.resolved ? 'ok' : 'warn'}
                        title={`${lastRetry.recordKey ?? 'The record'} — attempt ${lastRetry.result.retryCount}`}
                      >
                        <p>{lastRetry.result.message}</p>
                        <p style={{ marginTop: '0.5rem' }}>
                          <button type="button" className="btn btn--sm btn--ghost" onClick={() => setLastRetry(null)}>
                            Dismiss
                          </button>
                        </p>
                      </Alert>
                    ) : null}
                  </div>
                ) : null}

                {visibleErrors.length === 0 ? (
                  <div style={{ padding: '1.5rem' }}>
                    <EmptyState title="Nothing is waiting for a decision" icon="✓">
                      <p>
                        Every record that has arrived through a connector has been applied. Records that fail validation
                        appear here with the data exactly as it arrived, and stay until somebody retries them or writes
                        them off with a reason.
                      </p>
                    </EmptyState>
                  </div>
                ) : (
                  <DataTable
                    caption={includeClosed ? 'Every record in the error queue' : 'Records awaiting a decision'}
                    rows={visibleErrors}
                    rowKey={(e) => e.id}
                    pageSize={25}
                    searchValue={(e) =>
                      `${e.connector_name} ${e.connector_code} ${e.record_key ?? ''} ${e.error_code} ${e.error_message}`
                    }
                    searchLabel="Search the queue"
                    columns={[
                      {
                        key: 'connector',
                        header: 'Connector',
                        sortValue: (e) => e.connector_name,
                        render: (e) => (
                          <>
                            <div className="smaller">{e.connector_name}</div>
                            <div className="smaller muted">{dateTime(e.created_at)}</div>
                          </>
                        ),
                      },
                      {
                        key: 'record',
                        header: 'Record',
                        sortValue: (e) => e.record_key,
                        render: (e) => (
                          <>
                            <div className="mono smaller">{e.record_key ?? '—'}</div>
                            {e.run_reference ? <div className="smaller muted">from {e.run_reference}</div> : null}
                          </>
                        ),
                      },
                      {
                        key: 'problem',
                        header: 'What went wrong',
                        render: (e) => (
                          <>
                            {/* The sentence is what a person can act on; the
                                machine code is the footnote, not the headline. */}
                            <div>{e.error_message}</div>
                            <div className="smaller" style={{ marginTop: '0.25rem' }}>
                              <Badge tone="error">{statusLabel(e.error_code.toLowerCase())}</Badge>
                            </div>
                          </>
                        ),
                      },
                      {
                        key: 'payload',
                        header: 'The record',
                        render: (e) => {
                          const fields = Object.entries(e.payload);
                          return (
                            <details>
                              <summary className="smaller">Show what arrived</summary>
                              <div style={{ marginTop: '0.5rem' }}>
                                {fields.length === 0 ? (
                                  <p className="smaller muted">The record arrived with no fields.</p>
                                ) : (
                                  <DefinitionList
                                    items={fields.map(([key, value]): [ReactNode, ReactNode] => [
                                      <span className="smaller">{fieldLabel(key)}</span>,
                                      <span className="mono smaller">{payloadValue(value)}</span>,
                                    ])}
                                  />
                                )}
                              </div>
                            </details>
                          );
                        },
                      },
                      {
                        key: 'attempts',
                        header: 'Attempts',
                        sortValue: (e) => e.retry_count,
                        render: (e) => {
                          const scheduled = nextAttempt(e.next_retry_at);
                          return (
                            <>
                              <Badge tone={ERROR_STATUS_TONES[e.status] ?? 'neutral'}>{statusLabel(e.status)}</Badge>
                              <div className="smaller muted">
                                {e.retry_count} {e.retry_count === 1 ? 'attempt' : 'attempts'} so far
                              </div>
                              {scheduled ? <div className="smaller muted">{scheduled}</div> : null}
                              {e.resolution_note ? (
                                <div className="smaller muted">
                                  {e.resolution_note}
                                  {e.resolved_by_name ? ` — ${e.resolved_by_name}` : ''}
                                </div>
                              ) : null}
                            </>
                          );
                        },
                      },
                      {
                        key: 'action',
                        header: <span className="visually-hidden">Decision</span>,
                        render: (e) =>
                          // A record already resolved or written off has had its
                          // decision made; offering the buttons again would be a
                          // control that cannot do anything.
                          can('integration.run') && AWAITING_DECISION.includes(e.status) ? (
                            <div className="btn-group">
                              <button
                                type="button"
                                className="btn btn--sm"
                                disabled={retry.busy}
                                onClick={() => startRetry(e)}
                              >
                                Retry
                              </button>
                              <button
                                type="button"
                                className="btn btn--sm btn--ghost"
                                title="Close this record with a recorded reason"
                                onClick={() => {
                                  setWriteOffFor(e);
                                  setReason('');
                                  writeOff.clearError();
                                }}
                              >
                                Write off
                              </button>
                            </div>
                          ) : null,
                      },
                    ]}
                  />
                )}
              </>
            )}
          </AsyncPanel>
        </Card>
      )}

      <Modal
        open={writeOffFor !== null}
        onClose={() => setWriteOffFor(null)}
        title="Write off this record"
        footer={
          <>
            <button type="button" className="btn" onClick={() => setWriteOffFor(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={writeOff.busy || reason.trim().length < 10}
              onClick={confirmWriteOff}
            >
              {writeOff.busy ? 'Recording…' : 'Record the decision'}
            </button>
          </>
        }
      >
        <p className="muted">
          Writing off {writeOffFor?.record_key ? <span className="mono">{writeOffFor.record_key}</span> : 'this record'}{' '}
          closes it without applying it. Nothing is deleted: the record, the reason it failed and the reason it was
          written off stay in the queue and in the audit trail.
        </p>
        <ErrorNotice error={writeOff.error} />
        <TextArea
          label="Why is this record being written off?"
          hint="At least ten characters. This is recorded against the record and shown to anyone who looks at it later."
          required
          rows={4}
          value={reason}
          onChange={(event) => setReason(event.currentTarget.value)}
        />
      </Modal>

      <Modal
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected ? selected.name : 'Connector'}
        wide
        footer={
          <button type="button" className="btn" onClick={() => setSelected(null)}>
            Close
          </button>
        }
      >
        <AsyncPanel state={detail}>
          {(data) => (
            <div className="stack">
              {data.isDemonstrationAdapter ? (
                <Alert tone="warn" title="Demonstration adapter">
                  <p>
                    {data.adapterDescription ?? data.connector.description}
                  </p>
                  <p className="smaller" style={{ marginTop: '0.5rem' }}>
                    This adapter contacts nothing. It produces deterministic records locally so that the mapping,
                    validation and reconciliation below can be shown working without any external credential.
                  </p>
                </Alert>
              ) : null}

              <DefinitionList
                items={[
                  ['Vendor', data.connector.vendor],
                  ['Category', categoryLabel(data.connector.category)],
                  ['Direction', label(DIRECTION_LABELS, data.connector.direction)],
                  ['Transport', label(TRANSPORT_LABELS, data.connector.transport)],
                  ['Schedule', data.connector.schedule_description ?? 'On demand'],
                  ['Health', <Badge tone={statusTone(data.connector.health_status)}>{statusLabel(data.connector.health_status)}</Badge>],
                  ['Last succeeded', data.connector.last_success_at ? dateTime(data.connector.last_success_at) : 'Never'],
                  ['Last failed', data.connector.last_failure_at ? dateTime(data.connector.last_failure_at) : 'Never'],
                  ['Retry limit', `${number(data.connector.max_retries)} attempts, with a widening gap between them`],
                ]}
              />

              <Card title="Field mapping" subtitle="How each field from the far system lands here." flush>
                <DataTable
                  caption={`Field mapping for ${data.connector.name}`}
                  rows={data.mappings}
                  rowKey={(m) => m.id}
                  emptyMessage="No field mapping has been configured for this connector."
                  columns={[
                    { key: 'source', header: 'Field as it arrives', render: (m) => <span className="mono smaller">{m.source_field}</span> },
                    {
                      key: 'target',
                      header: 'Where it lands',
                      render: (m) => (
                        <>
                          <div className="mono smaller">{m.target_field}</div>
                          {m.target_table ? <div className="smaller muted">{targetTableLabel(m.target_table)}</div> : null}
                        </>
                      ),
                    },
                    {
                      key: 'transform',
                      header: 'Treatment',
                      render: (m) => (
                        <>
                          <div className="smaller">{label(TRANSFORM_LABELS, m.transform)}</div>
                          {m.transform_argument ? <div className="smaller muted mono">{m.transform_argument}</div> : null}
                        </>
                      ),
                    },
                    {
                      key: 'required',
                      header: 'Required',
                      render: (m) =>
                        m.is_required ? <Badge tone="info">Required</Badge> : <span className="muted smaller">Optional</span>,
                    },
                    {
                      key: 'default',
                      header: 'If it is missing',
                      render: (m) =>
                        m.default_value ? <span className="mono smaller">{m.default_value}</span> : <span className="muted">—</span>,
                    },
                  ]}
                />
              </Card>

              <Card
                title="Run history"
                subtitle="Every transfer reconciles the total the source declared against the total applied here."
                flush
              >
                <DataTable
                  caption={`Run history for ${data.connector.name}`}
                  rows={data.runs}
                  rowKey={(r) => r.id}
                  emptyMessage="This connector has not been run yet."
                  columns={[
                    {
                      key: 'ref',
                      header: 'Run',
                      render: (r) => (
                        <>
                          <div className="mono smaller">{r.run_reference}</div>
                          <div className="smaller muted">{label(TRIGGER_LABELS, r.trigger)}</div>
                        </>
                      ),
                    },
                    {
                      key: 'started',
                      header: 'Started',
                      sortValue: (r) => r.started_at,
                      render: (r) => (
                        <>
                          <div className="smaller">{dateTime(r.started_at)}</div>
                          {r.triggered_by_name ? <div className="smaller muted">{r.triggered_by_name}</div> : null}
                        </>
                      ),
                    },
                    { key: 'read', header: 'Read', numeric: true, render: (r) => number(r.records_read) },
                    { key: 'accepted', header: 'Accepted', numeric: true, render: (r) => number(r.records_accepted) },
                    {
                      key: 'rejected',
                      header: 'Queued',
                      numeric: true,
                      render: (r) =>
                        r.records_rejected > 0 ? (
                          <Badge tone="error">{number(r.records_rejected)}</Badge>
                        ) : (
                          <span className="muted">—</span>
                        ),
                    },
                    {
                      key: 'declared',
                      header: 'Declared by the source',
                      numeric: true,
                      render: (r) => <Money minor={Number(r.control_total_out_minor)} />,
                    },
                    {
                      key: 'applied',
                      header: 'Applied here',
                      numeric: true,
                      render: (r) => <Money minor={Number(r.control_total_in_minor)} />,
                    },
                    {
                      key: 'reconciled',
                      header: 'Control totals',
                      render: (r) =>
                        r.reconciled ? (
                          <Badge tone="ok">Reconciled</Badge>
                        ) : (
                          <>
                            <Badge tone="error">Out of balance</Badge>
                            <div className="smaller num--negative">
                              <Money
                                minor={Math.abs(Number(r.control_total_out_minor) - Number(r.control_total_in_minor))}
                              />
                            </div>
                          </>
                        ),
                    },
                    {
                      key: 'status',
                      header: 'Outcome',
                      render: (r) => (
                        <>
                          <Badge tone={statusTone(r.status)}>{statusLabel(r.status)}</Badge>
                          {r.error_message ? <div className="smaller num--negative">{r.error_message}</div> : null}
                        </>
                      ),
                    },
                  ]}
                />
              </Card>
            </div>
          )}
        </AsyncPanel>
      </Modal>
    </>
  );
}
